import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormBySchema, FormFieldLayout, Item, useAPIWrapper } from 'drf-react-by-schema';

import { useAppSelector } from 'src/app/hooks';
import { appDispatch } from 'src/utils/utils';
import { setFormIsValid, setHandleSubmit, setPage } from 'src/features/pageSlice';
import { FlocosProductionStatus, GENERAL_ADMINS } from 'src/constants';
import { useSearchParams } from 'react-router-dom';
import { PageType } from 'src/constants/PageType';
import { Layout } from 'src/styles';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

const MODEL = 'flocos_production';
const key = MODEL;

const WIZARD_OPTIONS: (
  params: {
    perfil?: Item;
    status?: FlocosProductionStatus;
    isAdmin?: boolean;
    isMobile?: boolean;
  } | void
) => {
  FIELD_LAYOUT: FormFieldLayout;
  AUTOFOCUS_FIELD?: string;
  DEFAULTS?: Item;
  NEW_STATUS?: FlocosProductionStatus;
}[] = (params) => [
  {
    FIELD_LAYOUT: {
      title: 'Coleta do Coco',
      rows: ['data_coleta', 'perfil', 'ajudantes'],
    },
    AUTOFOCUS_FIELD: undefined,
    DEFAULTS: { perfil: params?.isAdmin ? null : params?.perfil, data_coleta: new Date() },
    NEW_STATUS: !params?.status ? FlocosProductionStatus.IN_PRODUCTION : undefined,
  },
  {
    FIELD_LAYOUT: {
      title: 'Pesagem do floco úmido',
      rows: ['data_flocos_umido', 'peso_flocos_umido'],
    },
    AUTOFOCUS_FIELD: 'peso_flocos_umido',
    NEW_STATUS: !params?.status ? FlocosProductionStatus.IN_PRODUCTION : undefined,
    DEFAULTS: { data_flocos_umido: new Date() },
  },
  {
    FIELD_LAYOUT: {
      title: 'Pesagem do floco seco',
      rows: ['data_flocos_seco', 'peso_flocos_seco'],
    },
    AUTOFOCUS_FIELD: 'peso_flocos_seco',
    NEW_STATUS:
      !params?.status || params.status === FlocosProductionStatus.IN_PRODUCTION
        ? FlocosProductionStatus.READY_TO_REVIEW
        : undefined,
    DEFAULTS: { data_flocos_seco: new Date() },
  },
  ...(params?.isMobile
    ? []
    : [
        {
          FIELD_LAYOUT: {
            title: 'Situação da produção',
            rows: ['status'],
          },
          AUTOFOCUS_FIELD: undefined,
        },
      ]),
];

function objectReducer(array: Item[], key: string) {
  let reduced: Item = {};
  for (const item of array) {
    reduced = {
      ...reduced,
      ...item[key],
    };
  }
  return reduced;
}

export default function FlocosProductionForm() {
  const { perfil, id, type, formWizard, formValues, isMobile } = useAppSelector(
    (state) => state.page
  );
  const { pageForm, loadSinglePageData, onEditModel } = useAPIWrapper();
  const [isAdmin] = useHasPermissionsMulti([GENERAL_ADMINS]);
  const [URLSearchParams] = useSearchParams();
  const page = parseInt(URLSearchParams.get('page') || '1');
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
    register,
  } = useForm({
    mode: 'onBlur',
    resolver: pageForm?.validationSchema ? yupResolver(pageForm.validationSchema) : undefined,
  });

  const hiddenFields = ['id', ...(isMobile ? ['status'] : [])];

  const fieldsLayout = useMemo(() => {
    const allWizardOptions = WIZARD_OPTIONS({ perfil: perfil || undefined, isAdmin });
    const wizardOptions = allWizardOptions[page - 1];
    return isMobile
      ? [wizardOptions.FIELD_LAYOUT]
      : allWizardOptions.map(({ FIELD_LAYOUT }) => FIELD_LAYOUT);
  }, [page, isMobile, perfil, isAdmin]);

  const autoFocusField = useMemo(() => {
    const allWizardOptions = WIZARD_OPTIONS({ perfil: perfil || undefined });
    const wizardOptions = allWizardOptions[page - 1];
    return isMobile ? wizardOptions.AUTOFOCUS_FIELD : allWizardOptions[0].AUTOFOCUS_FIELD;
  }, [page, isMobile, perfil]);

  useEffect(() => {
    if (!perfil || !fieldsLayout || !type || (type === PageType.EDIT && !id)) {
      return;
    }
    const loadAllDataAsync = async () => {
      const pageTitle = 'Produção de flocos de mesocarpo';
      const allWizardOptions = WIZARD_OPTIONS({ perfil, isAdmin });
      const wizardOptions = allWizardOptions[page - 1];

      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
        defaults: isMobile ? wizardOptions.DEFAULTS : objectReducer(allWizardOptions, 'DEFAULTS'),
        fieldsLayout,
      });

      if (values) {
        const currentStatus = values?.status?.value as FlocosProductionStatus;

        // Define status if necessary
        const newStatus = WIZARD_OPTIONS({ status: currentStatus })[page - 1].NEW_STATUS;
        if (isMobile && newStatus) {
          values.status.value = newStatus;
        }

        // Populate the form
        reset(values);

        appDispatch(
          setPage({
            model: MODEL,
            title: pageTitle,
            formValues: values,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, perfil, isMobile, page, fieldsLayout, type, isAdmin]);

  useEffect(() => {
    if (!isMobile || !type || ![PageType.EDIT, PageType.CREATE].includes(type)) {
      return;
    }
    if (!formWizard) {
      const newSubmitSuccessTarget = `/${key}/view/{ID}/?type=${type}`;
      const newFormWizard = {
        pages: WIZARD_OPTIONS().length,
        submitSuccessTarget: newSubmitSuccessTarget,
      };
      appDispatch(setPage({ formWizard: newFormWizard }));
    }
  }, [formWizard, type, id, isMobile]);

  useEffect(() => {
    appDispatch(
      setPage({
        formDisabled: !isDirty,
      })
    );
  }, [isDirty]);

  useEffect(() => {
    appDispatch(setFormIsValid(isValid));
  }, [isValid]);

  useEffect(() => {
    appDispatch(setHandleSubmit(handleSubmit));
  }, [handleSubmit]);

  if (!pageForm?.schema || !formValues || !type || !fieldsLayout) {
    return null;
  }

  return (
    <FormBySchema
      fieldsLayout={fieldsLayout}
      autoFocusField={autoFocusField}
      hiddenFields={hiddenFields}
      control={control}
      register={register}
      errors={errors}
      schema={pageForm.schema}
      onEditModel={onEditModel}
      getValues={getValues}
      setValue={setValue}
      elevation={0}
      {...Layout.formLayout}
    />
  );
}
